import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, authFetch } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageSEO } from '../components/PageSEO';
import { RegisterModal } from '../components/RegisterModal';
import { ToolFeedback } from '../components/ToolFeedback';
import { EmailVerificationWall } from '../components/EmailVerificationWall';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { PositionCombobox } from '../components/PositionCombobox';
import { toast } from 'sonner';
import {
  Briefcase,
  Loader2,
  CheckCircle2,
  TrendingUp,
  DollarSign,
  Award,
  AlertTriangle,
  ShieldAlert,
  BadgeCheck,
  Building2,
  MapPin,
  Lock,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const seniorities = [
  { value: 'junior', label: 'Junior (0-2 años)' },
  { value: 'mid', label: 'Mid-Level (3-5 años)' },
  { value: 'senior', label: 'Senior (6-10 años)' },
  { value: 'lead', label: 'Lead / Gerente (10+ años)' },
];

const contractTypes = [
  { value: 'indefinido', label: 'Contrato Indefinido' },
  { value: 'determinado', label: 'Contrato por Tiempo Determinado' },
  { value: 'obra', label: 'Contrato por Obra' },
  { value: 'honorarios', label: 'Honorarios / Freelance' },
];

const geographicZones = [
  { value: 'general', label: 'Zona General' },
  { value: 'frontera_norte', label: 'Zona Libre de la Frontera Norte' },
];

const BREAKDOWN_LABELS = {
  salario_bruto_mensual: 'Salario bruto mensual',
  salario_bruto_anual: 'Salario bruto anual',
  isr_estimado_anual: 'ISR estimado anual',
  salario_neto_mensual: 'Salario neto mensual',
  bono_anual: 'Bono anual',
  comisiones_anuales: 'Comisiones anuales',
  bono_firma: 'Bono de firma',
  valor_equity: 'Valor de equity',
  valor_prestaciones_mensual: 'Valor de prestaciones (mensual)',
  compensacion_total_anual: 'Compensación total anual',
};

// Conceptos que se quedan detrás de Pro una vez que el resultado difuminado reemplazó
// el límite de usos (ver instrucción de sept. 2026) — el resto del desglose y del salario
// base se muestran siempre en claro.
const BLURRED_BREAKDOWN_KEYS = new Set([
  'bono_anual',
  'comisiones_anuales',
  'bono_firma',
  'valor_equity',
  'valor_prestaciones_mensual',
  'compensacion_total_anual',
]);

// No confiar en blur() de CSS para ocultar el valor real: en Liquidación un número grande
// y en negrita sobre fondo oscuro seguía siendo legible a simple vista con blur-sm (bug
// real en prod, sept. 2026). Se sustituye el valor por un marcador fijo en vez de
// difuminar el real — no hay dígitos ni texto real en el DOM que blurear.
const MASKED_CURRENCY = '$•••,•••';
const MASKED_NUMBER = '••';
const MASKED_TEXT = '••••••';

export default function JobEvaluatorPage() {
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const pendingSubmitRef = useRef(false);

  const [formData, setFormData] = useState({
    role: '',
    city: '',
    seniority: '',
    salary: '',
    bonus: '',
    commissions: '',
    signing_bonus: '',
    equity: '',
    contract_type: '',
    geographic_zone: 'general',
    health_insurance: false,
    dental_insurance: false,
    life_insurance: false,
    savings_fund: false,
    grocery_vouchers: false,
    remote_work: false,
  });

  const isPro = user?.plan === 'pro';
  const isBlurred = !!result && !isPro;

  // Registra en usage_limit_events cada vez que un usuario no-Pro ve la versión
  // difuminada del resultado (reemplaza a "limit_reached" para esta herramienta, que ya
  // no aplica: no hay límite de usos, el difuminado es el mecanismo permanente).
  useEffect(() => {
    if (isBlurred) {
      authFetch(`${API_URL}/api/usage-limit/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tool: 'job_evaluator', event: 'blurred_result_view' }),
      }).catch(() => { /* non-blocking */ });
    }
  }, [result, isPro]);

  const trackUpgradeClick = () => {
    authFetch(`${API_URL}/api/usage-limit/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tool: 'job_evaluator', event: 'upgrade_click' }),
    }).catch(() => { /* non-blocking */ });
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const canSubmit = () => {
    return formData.role && formData.city && formData.seniority && formData.salary && formData.contract_type;
  };

  const executeSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...formData,
        salary: parseFloat(formData.salary) || 0,
        bonus: parseFloat(formData.bonus) || 0,
        commissions: parseFloat(formData.commissions) || 0,
        signing_bonus: parseFloat(formData.signing_bonus) || 0,
        equity: parseFloat(formData.equity) || 0,
      };

      const response = await authFetch(`${API_URL}/api/evaluations/job-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Error al evaluar oferta');
      }

      const data = await response.json();
      setResult(data);
      toast.success('Oferta evaluada exitosamente');
    } catch (error) {
      toast.error(error.message || 'Error al evaluar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit()) return;

    if (!isAuthenticated) {
      pendingSubmitRef.current = true;
      setShowRegisterModal(true);
      return;
    }

    await executeSubmit();
  };

  const handleRegisterSuccess = (registeredUser) => {
    if (pendingSubmitRef.current) {
      pendingSubmitRef.current = false;
      // Cuenta recién creada, aún sin verificar: el muro se mostrará en el siguiente
      // render en vez de ejecutar la evaluación pendiente.
      if (registeredUser?.email_verified === false) return;
      executeSubmit();
    }
  };

  const getScoreClass = (score) => {
    if (score >= 80) return 'score-excellent';
    if (score >= 65) return 'score-good';
    if (score >= 50) return 'score-average';
    return 'score-low';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Layout>
      <PageSEO
        title="Evaluador de Oferta de Trabajo | Klarvi"
        description="Compara tu oferta laboral: salario neto, prestaciones y qué tan competitiva es frente al mercado en México. Analiza tu oferta antes de responder."
        canonical="https://klarvi.org/job-evaluator"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Evaluador de Oferta de Trabajo - Klarvi",
          "url": "https://klarvi.org/job-evaluator",
          "description": "Compara y evalúa una oferta laboral en México: salario neto, prestaciones y competitividad frente al mercado.",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "inLanguage": "es-MX",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "MXN" },
        }}
      />
      {isAuthenticated && user?.email_verified === false ? (
        <EmailVerificationWall />
      ) : (
      <>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="job-evaluator-page">
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A] font-['Manrope']">
            Evaluador de Oferta Laboral
          </h1>
          <p className="mt-2 text-muted-foreground">
            Analiza y compara ofertas de trabajo para tomar mejores decisiones.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <Card className="border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Manrope']">
                <Briefcase className="w-5 h-5 text-[#2563EB]" />
                Datos de la Oferta
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="role">Puesto</Label>
                    <PositionCombobox
                      value={formData.role}
                      onChange={(v) => updateField('role', v)}
                      placeholder="Ej: Desarrollador Frontend"
                      testId="job-role-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Los puestos marcados con <BadgeCheck className="w-3 h-3 inline text-teal-600" /> tienen banda salarial real de la Guía Salarial Buk 2026.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">Ciudad</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => updateField('city', e.target.value)}
                        placeholder="Ej: CDMX"
                        data-testid="job-city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="seniority">Nivel</Label>
                      <Select
                        value={formData.seniority}
                        onValueChange={(v) => updateField('seniority', v)}
                      >
                        <SelectTrigger data-testid="job-seniority-select">
                          <SelectValue placeholder="Seleccionar" />
                        </SelectTrigger>
                        <SelectContent>
                          {seniorities.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contract_type">Tipo de Contrato</Label>
                      <Select
                        value={formData.contract_type}
                        onValueChange={(v) => updateField('contract_type', v)}
                      >
                        <SelectTrigger data-testid="job-contract-select">
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {contractTypes.map((c) => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="geographic_zone">Zona Geográfica</Label>
                      <Select
                        value={formData.geographic_zone}
                        onValueChange={(v) => updateField('geographic_zone', v)}
                      >
                        <SelectTrigger data-testid="job-zone-select">
                          <SelectValue placeholder="Seleccionar zona" />
                        </SelectTrigger>
                        <SelectContent>
                          {geographicZones.map((z) => (
                            <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Compensation */}
                <div className="space-y-4">
                  <h4 className="font-medium text-[#0F172A]">Compensación</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="salary">Salario Mensual (MXN) *</Label>
                      <Input
                        id="salary"
                        type="number"
                        value={formData.salary}
                        onChange={(e) => updateField('salary', e.target.value)}
                        placeholder="45000"
                        data-testid="job-salary-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="bonus">Bono Anual</Label>
                      <Input
                        id="bonus"
                        type="number"
                        value={formData.bonus}
                        onChange={(e) => updateField('bonus', e.target.value)}
                        placeholder="0"
                        data-testid="job-bonus-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="commissions">Comisiones Mensuales</Label>
                      <Input
                        id="commissions"
                        type="number"
                        value={formData.commissions}
                        onChange={(e) => updateField('commissions', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signing_bonus">Bono de Firma</Label>
                      <Input
                        id="signing_bonus"
                        type="number"
                        value={formData.signing_bonus}
                        onChange={(e) => updateField('signing_bonus', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label htmlFor="equity">Equity / Acciones (valor)</Label>
                      <Input
                        id="equity"
                        type="number"
                        value={formData.equity}
                        onChange={(e) => updateField('equity', e.target.value)}
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Benefits */}
                <div className="space-y-4">
                  <h4 className="font-medium text-[#0F172A]">Prestaciones</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: 'health_insurance', label: 'Seguro de Gastos Médicos' },
                      { key: 'dental_insurance', label: 'Seguro Dental' },
                      { key: 'life_insurance', label: 'Seguro de Vida' },
                      { key: 'savings_fund', label: 'Fondo de Ahorro' },
                      { key: 'grocery_vouchers', label: 'Vales de Despensa' },
                      { key: 'remote_work', label: 'Trabajo Remoto' },
                    ].map((benefit) => (
                      <div key={benefit.key} className="flex items-center justify-between">
                        <Label htmlFor={benefit.key} className="text-sm cursor-pointer">
                          {benefit.label}
                        </Label>
                        <Switch
                          id={benefit.key}
                          checked={formData[benefit.key]}
                          onCheckedChange={(v) => updateField(benefit.key, v)}
                          data-testid={`job-${benefit.key}-switch`}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#2563EB] hover:bg-[#2563EB]/90"
                  disabled={!canSubmit() || loading}
                  data-testid="evaluate-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Evaluar Oferta
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          <div className="space-y-4">
            {result ? (
              <>
                {/* Legal Alert */}
                {result.legal_alert && (
                  <Card className="border-2 border-red-400 bg-red-50 animate-scale-in" data-testid="legal-alert-banner">
                    <CardContent className="p-4 flex items-start gap-3">
                      <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-800">Alerta: posible incumplimiento legal</p>
                        <p className="text-sm text-red-700 mt-1">{result.legal_alert.message}</p>
                        <p className="text-sm text-red-700 mt-2">
                          Si esto se confirma, puedes preguntarle al{' '}
                          <Link to="/advisor" className="font-medium underline hover:text-red-900">
                            Asesor IA
                          </Link>{' '}
                          cómo plantearlo antes de firmar o de hablar con RRHH.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Score Card */}
                <Card className="border animate-scale-in">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-[#0F172A] font-['Manrope']">
                          Puntuación de la Oferta
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {isBlurred ? MASKED_TEXT : result.market_position}
                        </p>
                      </div>
                      <div className={`score-indicator ${getScoreClass(result.offer_score)}`}>
                        {isBlurred ? MASKED_NUMBER : result.offer_score}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Combina tu salario frente al mercado y tus prestaciones — no es una calificación oficial, es una forma rápida de ver qué tan competitiva es esta oferta.
                    </p>
                    {result.matched_position ? (
                      <div className="mt-4 pt-4 border-t flex items-start gap-2 text-sm text-teal-700" data-testid="matched-position-info">
                        <BadgeCheck className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          Se comparó contra <strong>{result.matched_position}</strong> (Guía Salarial Buk 2026) —
                          cae en el percentil{' '}
                          {isBlurred ? MASKED_NUMBER : result.percentile_info?.percentile}
                          {' '}del mercado.
                        </span>
                      </div>
                    ) : (
                      <div className="mt-4 pt-4 border-t text-xs text-muted-foreground" data-testid="estimate-disclaimer">
                        {result.data_source || 'Estimación de mercado'}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Key Metrics */}
                <Card className="border animate-slide-up" style={{ animationDelay: '100ms' }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-['Manrope'] flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#2563EB]" />
                      Resumen Financiero
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                      <span className="text-sm text-muted-foreground">Ingreso Neto Mensual Estimado</span>
                      <span className="font-semibold text-[#0F172A]">
                        {formatCurrency(result.estimated_net_income)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-[#2563EB]/5 rounded-lg border border-[#2563EB]/20">
                      <span className="text-sm text-[#2563EB]">Compensación Total Anual</span>
                      <span className="font-bold text-[#2563EB]">
                        {isBlurred ? MASKED_CURRENCY : formatCurrency(result.total_compensation)}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Breakdown */}
                <Card className="border animate-slide-up" style={{ animationDelay: '200ms' }}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-['Manrope']">Desglose</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      {Object.entries(result.breakdown).map(([key, value]) => (
                        <div key={key} className="flex justify-between py-1 border-b border-border last:border-0">
                          <span className="text-muted-foreground">
                            {BREAKDOWN_LABELS[key] || key.replace(/_/g, ' ')}
                          </span>
                          <span className="font-medium">
                            {isBlurred && BLURRED_BREAKDOWN_KEYS.has(key) ? MASKED_CURRENCY : formatCurrency(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Pro upsell — reemplaza el límite de usos para esta herramienta */}
                {isBlurred && (
                  <div className="flex flex-wrap items-center justify-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800" data-testid="job-evaluator-pro-upsell">
                    <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span>Esto es solo una parte de lo que Klarvi puede decirte sobre esta oferta. Con Pro ves la puntuación completa y qué tan competitiva es de verdad frente al mercado.</span>
                    <Link to="/precios" onClick={trackUpgradeClick}>
                      <Button size="sm" className="bg-[#2563EB] hover:bg-[#2563EB]/90" data-testid="job-evaluator-upgrade-btn">
                        Actualizar a Pro
                      </Button>
                    </Link>
                  </div>
                )}

                {/* Next step */}
                <Card className="border-[#2563EB]/20 bg-[#2563EB]/5">
                  <CardContent className="p-4 text-sm">
                    <p className="font-medium text-[#0F172A] mb-1">¿Y ahora qué?</p>
                    <p className="text-muted-foreground">
                      Si el score es bajo o ves que estás por debajo del mercado, esto te sirve para negociar. Llévale esta oferta al{' '}
                      <Link to="/advisor" className="text-[#2563EB] hover:underline font-medium">
                        Asesor IA
                      </Link>{' '}
                      y prepara qué decir antes de responder.
                    </p>
                  </CardContent>
                </Card>

                {/* Disclaimer */}
                <div className="legal-disclaimer flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    Los cálculos son estimaciones basadas en información general y pueden variar según tu situación específica.
                  </span>
                </div>
                <ToolFeedback tool="evaluador_ofertas" />
              </>
            ) : (
              <Card className="border h-full min-h-[400px] flex items-center justify-center">
                <CardContent className="text-center">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <TrendingUp className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-[#0F172A] font-['Manrope']">
                    Evalúa tu oferta
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Completa el formulario para ver el análisis de la oferta laboral.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      <RegisterModal
        open={showRegisterModal}
        onOpenChange={setShowRegisterModal}
        onSuccess={handleRegisterSuccess}
      />
      </>
      )}
    </Layout>
  );
}
